import React, { useEffect } from 'react';
import { useSearchParams, useParams } from 'react-router-dom';
import PageLayout from '../../components/layout/PageLayout';
import ProgressBar from '../../components/common/ProgressBar';
import Pagination from '../../components/common/Pagination';
import SearchResultRow from '../../components/content/SearchResultRow';
import { useSearchCategoryPage, searchItemKey } from '../../features/search/useSearchCategoryPage';
import { ContentState } from '../../components/content/DiscoveryContent';
import { useWebLocale } from '../../contexts/WebLocaleContext';
import NotFound from '../NotFound';
import AppUiScope from '../../app-ui/AppUiScope';
import { useSponsoredPlacements } from '../../features/ads/useSponsoredPlacements';
import { BrowseInlineAd } from '../../features/browse/BrowseSponsored';
import { searchResultEntries } from '../../features/search/resultPresentation';
import SearchCategoryHeading from './components/SearchCategoryHeading';
import './SearchResultsApp.css';

const SearchResultDetailPage = () => {
  const { category } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get('q');
  const parsedPage = Number(searchParams.get('page'));
  const page = Number.isSafeInteger(parsedPage) && parsedPage > 0 ? parsedPage : 1;
  const { t } = useWebLocale();
  const result = useSearchCategoryPage(query, category, page);
  const campaigns = useSponsoredPlacements('SEARCH_RESULTS');
  const items = result.data || [];
  const loading = Boolean(query?.trim()) && result.isPending;
  const entries = searchResultEntries(items, campaigns, category);
  const totalPages = result.totalPages;
  useEffect(() => {
    if (!searchParams.get('page')) {
      const next = new URLSearchParams(searchParams);
      next.set('page', '1');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);
  const handlePageChange = (event, newPage) => {
    const next = new URLSearchParams(searchParams);
    next.set('page', newPage.toString());
    setSearchParams(next, { replace: false });
  };
  return (
    <PageLayout paddingTop={{ xs: '12px', md: '24px' }}>
      <AppUiScope className="pp-parity pp-browse pp-search-results" surface="lists">
        <SearchCategoryHeading category={category} query={query || ''} />
        <ProgressBar isLoading={loading}>
          <ContentState query={{ ...result, isPending: loading }}>
            {!items.length && <p>{t(query?.trim() ? 'empty' : 'searchIntro')}</p>}
            <div className="pp-search-result-grid">
              {entries.map((entry, index) =>
                entry.kind === 'sponsored' ? (
                  <div
                    key={`ad-${entry.campaign.id}-${entry.occurrence}`}
                    className={`pp-search-inline${category !== 'artists' && entries[index - 1]?.kind === 'organic' ? ' pp-search-inline-small-cover' : ''}`}
                  >
                    <BrowseInlineAd campaign={entry.campaign} />
                  </div>
                ) : (
                  <SearchResultRow
                    key={searchItemKey(entry.item)}
                    item={entry.item}
                    resultCategory={category}
                    resultPosition={
                      (page - 1) * result.size +
                      entries.slice(0, index + 1).filter((row) => row.kind === 'organic').length
                    }
                    kind={
                      category === 'reviews'
                        ? 'album'
                        : category === 'artists'
                          ? 'artist'
                          : 'playlist'
                    }
                  />
                ),
              )}
            </div>
            {totalPages > 1 && (
              <Pagination
                count={totalPages}
                page={Math.min(page, totalPages)}
                onChange={handlePageChange}
              />
            )}
          </ContentState>
        </ProgressBar>
      </AppUiScope>
    </PageLayout>
  );
};
// Unsupported categories must not mount queries or expose retired content.
export default function SearchResultDetail() {
  const { category } = useParams();
  return ['artists', 'reviews', 'lists', 'mixes'].includes(category) ? (
    <SearchResultDetailPage />
  ) : (
    <NotFound />
  );
}
